import React, {useEffect, useState} from 'react';
import {curry} from 'ramda';
import classNames from 'classnames';
import {Action} from 'redux-actions';
import {Link, useHistory} from 'react-router-dom';
import wrap from 'wraptile';
import {useDebouncedCallback} from 'use-debounce';
import {Box} from '@material-ui/core';
import {QcJobTestStatuses} from '@pluto/@types';
import {renderAssignedDate} from '@pluto/utils';
import {handleAutocompleteChange} from '@pluto/autocomplete';
import {getInitialRanges, DateRangeState} from '@pluto/daterange-picker';
import {getOptsFromQuery, handleSelect} from '@pluto/select';
import Table, {Sort, getInitialPage, getInitialRowsPerPage} from '@pluto/table';
import useQuery from '@pluto/query-hook';
import {setQuery} from '@pluto/set-history-query';
import {handlePreselect} from '@pluto/qc-preselect';
import {handleSort} from '@pluto/qc-sort';
import {handleQcManagerList as handleQcJobsList} from '@pluto/qc-paging';
import {handleDebouncedCallback, handleDebounce} from '@pluto/qc-debounce';
import {handleMount} from '@pluto/qc-mount';
import {makeHandleFilters} from '@pluto/qc-handle-filters';
import {handleDateRangeChange} from '@pluto/qc-handle-daterange';
import {ColumnType} from '@pluto/table/tableTypes';
import {SummaryContainer as QCJobsSummary, Section} from '@pluto/common/summary';
import {baseRoutes} from 'App/AppRouter/routes';
import {getUUID} from 'ContentItem/Search/SearchHandlers';
import {QCSpecificFilters, QCFilters, QCStatusEnum} from '../QCFilters/QCFiltersType';
import {
    QCFiltersParams,
    QCJobsListPayload,
    HandlePreselectChange,
    FilterHandlers,
    HandleChangeInputType,
    FiltersKeys,
    QCJobItemModified,
    GetQCJobsSummary,
    QCJobItem,
    QCManagerState,
} from '../QCManager/QCManagerTypes';
import {
    getInitialQCManagerSort,
    renderSelectAllCheckbox,
    renderCheckbox,
    setSearchParams,
} from '../QCManager/QCManagerContainer';
import QCJobsFilters from '../QCFilters/QCJobsFilters/QCJobsFiltersConnected';
import '../QCManager/QCManager.scss';
import {SectionTitles} from '../QCWorkFlowTypes';
import {getIsBeforeDate} from '../QCManager/QCManagerDialogs/DueDateQCModal/DueDateQCModalContainer';
import IssueCreateDialogContainer from '../QCJobs/QCJobsDialogs/JiraIssueCreate/IssueCreateDialogContainer';
import QCWorkQueueJobsControlButtons from './QCWorkQueueJobsControlButtons/QCWorkQueueJobsControlButtons';
import QCJobsColumns from './QCJobsColumns';
import JiraMenuContainer from './QCJobsDialogs/JiraMenu/JiraMenuContainer';
import SetStatusModalContainer from './QCWorkQueueSetStatusModal/QCWorkQueueSetStatusModalContainer';
import {CreateMultipleIssueParams} from './QCJobsDialogs/JiraMenu/JiraTypes';

export type CreateMultipleIssueType = (params: CreateMultipleIssueParams) => void;
export interface QCJobsContainerProps {
    setQCJobs: (params: QCFiltersParams) => Action<Promise<QCJobsListPayload>>;
    resetQCJobs: () => void;
    getQCJobsSummary: GetQCJobsSummary;
    createJiraMultipleIssue: CreateMultipleIssueType;
    qcManager: QCManagerState;
}

export const SCORE_BOARD_SECTIONS: Section[] = [
    {
        status: [QCStatusEnum.ReadyForQc],
        title: SectionTitles.ReadyForQc,
    },
    {
        status: [QCStatusEnum.InQc],
        title: SectionTitles.InQc,
    },
    {
        status: [QCStatusEnum.QcPassed, QCStatusEnum.QcFailed],
        title: SectionTitles.QcCompleteTwo,
        complete: true,
    },
];

const WEB_ORIGINAL_CONTENT_ITEM_TYPE = 'web_original';

export const renderJiraMenuButton = (item: QCJobItem) => {
    const {issues, title, priority, contentPartner, id, contentItem} = item;
    const {labPartner} = contentItem;

    if (labPartner) {
        return (
            <JiraMenuContainer
                issues={issues}
                title={title}
                priority={priority}
                contentPartner={contentPartner as any}
                labPartner={labPartner}
                id={id}
            />
        );
    }

    return null;
};

export const renderQCJobStatusLink = curry(
    (params: QCFiltersParams, item: QCJobItemModified) => {
        return <SetStatusModalContainer item={item} params={params} />;
    }
);

export const renderQCReportLink = (
    item: QCJobItemModified,
    endpoint?: string
): JSX.Element => {
    const {id, contentItem} = item;
    const {mamId, displayTitle} = contentItem;
    const rootId = endpoint || 'qc-work-queue';
    const url = `${rootId}/${id}`;
    return (
        <div className="search-table-item-name">
            <Link to={url}>{displayTitle}</Link>
            <div className="uuid">{getUUID(mamId)}</div>
        </div>
    );
};

export const handleJiraDialogOpen = wrap(
    (setJiraOpenDialog: (value: boolean) => void) => {
        setJiraOpenDialog(true);
    }
);

export const handleJiraDialogClose = wrap(
    (setJiraOpenDialog: (value: boolean) => void) => {
        setJiraOpenDialog(false);
    }
);

export const formatCheckedItems = curry((status: QcJobTestStatuses, id: number) => ({
    id,
    status,
}));

export const handleIsChecked = curry(
    (statusValue: QcJobTestStatuses, radioButtonValue: QcJobTestStatuses) => {
        return statusValue === radioButtonValue;
    }
);

export const getHighestPriority = (
    data: QCJobItemModified[],
    checkedItemsIds: number[]
) => {
    const checkedDataItems = data.reduce((acc: number[], curr: QCJobItemModified) => {
        if (checkedItemsIds.includes(curr.id)) {
            acc.push(curr.priority);
        }
        return acc.sort();
    }, []);

    return checkedDataItems[0];
};

const getCheckedLabPartners = (rows: QCJobItemModified[], checkedItems: number[]) => {
    return rows.reduce((acc: string[], curr: QCJobItemModified) => {
        if (checkedItems.includes(curr.id)) {
            acc.push(curr.labPartner);
        }
        return acc;
    }, []);
};

export const checkLabPartners = (rows: QCJobItemModified[], checkedItems: number[]) => {
    const uniqueLabPartners = new Set(getCheckedLabPartners(rows, checkedItems));

    if (uniqueLabPartners.size > 1) return true;

    for (const row of rows) {
        if (
            checkedItems.includes(row.id) &&
            row.contentType === WEB_ORIGINAL_CONTENT_ITEM_TYPE
        ) {
            return true;
        }
    }

    return false;
};

export const renderDueDate = (item: QCJobItem) => {
    const {dueDate} = item;
    const isBeforeDate = getIsBeforeDate(dueDate);
    return (
        <div
            className={classNames('due-date__container', {
                isBeforeDate,
            })}
        >
            {dueDate}
        </div>
    );
};

export default function QCJobsContainer(props: QCJobsContainerProps) {
    const {
        setQCJobs,
        resetQCJobs,
        getQCJobsSummary,
        createJiraMultipleIssue,
        qcManager,
    } = props;
    const {total, rows} = qcManager;
    const query = useQuery();
    const getFromQuery = getOptsFromQuery(query);
    const history = useHistory();
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const DEBOUNCE_TIME = 700;
    const [page, setPage] = useState<number>(initialPage);
    const [jiraDialogIsOpen, setJiraOpenDialog] = useState(false);
    const initialSort = getInitialQCManagerSort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    const [title, setTitle] = useState<string>(query.get('title') || '');
    const [checkedItems, setCheckedItems] = useState<number[]>([]);
    const initialDueDate = getInitialRanges(query, 'dateStart', 'dateEnd');
    const initialFinishDate = getInitialRanges(query, 'finishDateStart', 'finishDateEnd');
    const [dueDates, setDueDates] = useState<DateRangeState>(initialDueDate);
    const [finishDates, setFinishDates] = useState<DateRangeState>(initialFinishDate);
    const [filters, setFilters] = useState<QCSpecificFilters>({
        qcJobType: query.get('qcJobType') || '',
        qcJobStatus: getFromQuery('qcJobStatus'),
        contentItemType: getFromQuery('contentItemType'),
        contentPartnerId: getFromQuery('contentPartnerId'),
    });
    const params: QCFilters = {
        ...filters,
        ...sort,
        title,
        finishDates,
        dates: dueDates,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };
    const allJobsItemsIds = rows.map((row) => row.id);

    const columns: ColumnType[] = [
        {
            dataKey: '',
            title: renderSelectAllCheckbox(
                {
                    checkedItems,
                    setCheckedItems,
                },
                allJobsItemsIds
            ),
            render: renderCheckbox({
                checkedItems,
                setCheckedItems,
            }),
        },
        {
            dataKey: 'priority',
            title: 'Prio',
            sortable: true,
        },
        {
            dataKey: 'title',
            title: 'Title',
            sortable: true,
            render: renderQCReportLink,
        },
        {
            dataKey: 'dueDate',
            title: 'Due date',
            sortable: true,
            render: renderDueDate,
        },
        ...QCJobsColumns,
        {
            dataKey: 'assignedAt',
            title: 'Date assigned',
            render: renderAssignedDate,
            sortable: true,
        },
        {
            dataKey: 'status',
            title: 'Status',
            render: renderQCJobStatusLink(params),
        },
        {
            dataKey: 'jira',
            title: '',
            render: renderJiraMenuButton,
        },
    ];

    const debouncedCallback = handleDebouncedCallback({
        ...filters,
        ...sort,
        title,
        limit: rowsPerPage,
        offset: 0,
        setQCJobs,
        setPage,
        setQuery,
        history,
        route: baseRoutes.qcJobs,
    });
    const [debouncedFunction] = useDebouncedCallback(debouncedCallback, DEBOUNCE_TIME);

    const onDebouncedSearch: HandleChangeInputType = handleDebounce({
        debouncedFunction,
        setTitle,
    });

    const filterHandlers: FilterHandlers = {
        ...sort,
        setFilters,
        setPage,
        setQuery,
        history,
        setQCJobs,
        filters,
        rowsPerPage,
        title,
        route: baseRoutes.qcJobs,
        setDueDates,
        setFinishDates,
        dueDates,
        finishDates,
    };

    const onPreselectClick: HandlePreselectChange = handlePreselect(filterHandlers);

    const handleFilters = makeHandleFilters(filterHandlers);

    const handleTypeChange = handleFilters(FiltersKeys.Type);
    const handleContentPartnersChange = handleAutocompleteChange({
        ...filterHandlers,
        key: FiltersKeys.ContentPartnerId,
    });
    const handleStatusChange = handleSelect({
        ...filterHandlers,
        key: FiltersKeys.Status,
    });

    const handleContentTypesChange = handleAutocompleteChange({
        ...filterHandlers,
        key: FiltersKeys.ContentType,
    });

    const onPaging = handleQcJobsList(params, {
        setQCJobs,
        setQuery,
        history,
        route: baseRoutes.qcJobs,
    });

    const onMount = handleMount({
        setQCJobs,
        resetQCJobs,
        getQCJobsSummary,
        setRowsPerPage,
        setPage,
        params,
        query,
    });

    useEffect(onMount, []);
    useEffect(setSearchParams(query), [query]);

    const onSort = handleSort({
        setSort,
        setQCJobs,
        params,
        setQuery,
        history,
        route: baseRoutes.qcJobs,
    });

    const onDueDateRangeSelect = handleDateRangeChange(filterHandlers, 'dueDate');
    const onFinishDateRangeSelect = handleDateRangeChange(filterHandlers, 'finishDate');
    const priority = getHighestPriority(rows, checkedItems);
    const isJiraCreationDisabled = checkLabPartners(rows, checkedItems);

    return (
        <Box className="qc-jobs">
            <QCJobsSummary
                status={filters.qcJobStatus}
                setFilter={onPreselectClick}
                setDates={setFinishDates}
                sections={SCORE_BOARD_SECTIONS}
                state={qcManager}
            />
            <QCJobsFilters
                filters={filters}
                title={title}
                handleTitleChange={onDebouncedSearch}
                handleTypeChange={handleTypeChange}
                handleContentPartnersChange={handleContentPartnersChange}
                handleContentTypesChange={handleContentTypesChange}
                handleStatusChange={handleStatusChange}
                onDueDateRangeSelect={onDueDateRangeSelect}
                onFinishDateRangeSelect={onFinishDateRangeSelect}
                dueDates={dueDates}
                finishDates={finishDates}
            />
            <IssueCreateDialogContainer
                createMultipleIssue={createJiraMultipleIssue}
                dialogIsOpen={jiraDialogIsOpen}
                handleDialogClose={handleJiraDialogClose(
                    setJiraOpenDialog,
                    setCheckedItems
                )}
                items={checkedItems}
                data={rows}
                priority={priority}
                setCheckedItems={setCheckedItems}
            />
            <Table
                data={rows}
                columns={columns}
                setSort={onSort}
                sort={sort}
                total={total}
                onChangePageHandler={onPaging}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
            >
                <QCWorkQueueJobsControlButtons
                    handleMultiItemsNewJiraIssue={handleJiraDialogOpen(setJiraOpenDialog)}
                    checkedItems={checkedItems}
                    rowsLength={rows.length}
                    isJiraCreationDisabled={isJiraCreationDisabled}
                />
            </Table>
        </Box>
    );
}
